/**
 * LeadDetails — read-only view of a prospect that hasn't been billed yet.
 *
 * Why a separate page? Leads live in `booking_leads`, not `customers`. The
 * existing CustomerDetails page assumes a real customer doc (vehicles
 * collection, invoice history, customer-level financials). A lead has
 * vehicles INSIDE its own doc and zero invoice history — so a dedicated
 * page keeps both UIs honest.
 *
 * Surface:
 *   • Identity block (name, mobile, email, address, source, created date)
 *   • Vehicles array (rego / make / model — captured at booking time)
 *   • Bookings + Job Cards that reference this lead (so SM can see what's
 *     pending)
 *   • CTA: "Promote to Customer" (manual promotion — auto-fires at invoice
 *     creation, this is for when SM wants to flag a VIP early)
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, User, Phone, Mail, MapPin, Car, Calendar, FileText, UserCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LeadDetails() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [promoting, setPromoting] = useState(false);

  // Fetch the auth'd user so <Layout user={user}> can render the sidebar
  // (mechanic vs admin role gates the menu items).
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
        if (r.ok) setUser(await r.json());
      } catch (e) { /* ignore — Layout falls back to no sidebar */ }
    })();
  }, []);

  const fetchLead = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/leads/${leadId}`, { credentials: 'include' });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to load lead');
        return;
      }
      const data = await r.json();
      setLead(data);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load lead');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLead();   }, [leadId]);

  const promote = async () => {
    setPromoting(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/leads/${leadId}/promote`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await r.json();
      if (!r.ok) {
        toast.error(data.detail || 'Promotion failed');
        return;
      }
      toast.success('Lead promoted — now a real customer');
      navigate(`/customers/${data.customer_id}`);
    } catch (e) {
      console.error(e);
      toast.error('Promotion failed');
    } finally {
      setPromoting(false);
    }
  };

  if (loading) {
    return <Layout user={user}><div className="p-6 text-slate-500">Loading…</div></Layout>;
  }
  if (!lead) {
    return <Layout user={user}><div className="p-6 text-slate-500">Lead not found</div></Layout>;
  }

  return (
    <Layout user={user}>
      <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="lead-details-page">
        <div className="flex items-center justify-between gap-4">
          <Button variant="ghost" onClick={() => navigate('/customers')} data-testid="back-to-customers-btn">
            <ArrowLeft className="h-4 w-4 mr-2" />Back to Customers
          </Button>
          <Button onClick={promote} disabled={promoting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="promote-lead-btn">
            <UserCheck className="h-4 w-4 mr-2" />
            {promoting ? 'Promoting…' : 'Promote to Customer'}
          </Button>
        </div>

        <Card data-testid="lead-identity-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <User className="h-5 w-5 text-amber-600" />
              {lead.display_name || `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'Unnamed lead'}
              <Badge
                className="bg-amber-100 text-amber-800 border border-amber-200 uppercase tracking-wide"
                data-testid="lead-status-badge"
              >
                Lead
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-slate-400" />
              <span data-testid="lead-mobile">{lead.mobile || lead.phone || '-'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-slate-400" />
              <span data-testid="lead-email">{lead.email || '-'}</span>
            </div>
            {(lead.address_line1 || lead.suburb || lead.postcode) && (
              <div className="md:col-span-2 flex items-start gap-2" data-testid="lead-address">
                <MapPin className="h-4 w-4 text-slate-400 mt-0.5" />
                <span>
                  {[lead.address_line1, lead.address_line2, lead.suburb, lead.state, lead.postcode]
                    .filter(Boolean).join(', ')}
                </span>
              </div>
            )}
            <div className="text-slate-500">
              <span className="font-medium text-slate-700">Source:</span> {lead.source || 'unknown'}
            </div>
            <div className="text-slate-500">
              <span className="font-medium text-slate-700">Created:</span>{' '}
              {lead.created_at ? new Date(lead.created_at).toLocaleDateString('en-AU') : '-'}
            </div>
          </CardContent>
        </Card>

        <Card data-testid="lead-vehicles-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><Car className="h-4 w-4" />Vehicles ({(lead.vehicles || []).length})</CardTitle>
          </CardHeader>
          <CardContent>
            {(lead.vehicles || []).length === 0 ? (
              <p className="text-slate-500 text-sm">No vehicles captured yet.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {lead.vehicles.map((v, i) => (
                  <li key={`${v.rego}-${i}`} className="py-2 text-sm flex items-center gap-3">
                    <span className="font-mono font-semibold text-slate-900">{v.rego || '—'}</span>
                    <span className="text-slate-600">{[v.make, v.model, v.year].filter(Boolean).join(' ')}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card data-testid="lead-bookings-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><Calendar className="h-4 w-4" />Bookings ({(lead.bookings || []).length})</CardTitle>
          </CardHeader>
          <CardContent>
            {(lead.bookings || []).length === 0 ? (
              <p className="text-slate-500 text-sm">No bookings linked.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {lead.bookings.map((b) => (
                  <li key={b.booking_id} className="py-2 text-sm flex items-center justify-between">
                    <span>
                      <span className="font-semibold text-slate-900">{b.booking_number}</span>
                      {b.vehicle_rego && <span className="text-slate-500 ml-2">{b.vehicle_rego}</span>}
                    </span>
                    <span className="text-slate-500 text-xs">{b.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card data-testid="lead-jcs-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><FileText className="h-4 w-4" />Job Cards ({(lead.job_cards || []).length})</CardTitle>
          </CardHeader>
          <CardContent>
            {(lead.job_cards || []).length === 0 ? (
              <p className="text-slate-500 text-sm">No job cards yet.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {lead.job_cards.map((jc) => (
                  <li key={jc.job_card_id} className="py-2 text-sm flex items-center justify-between">
                    <span>
                      <span className="font-semibold text-slate-900">{jc.job_card_number}</span>
                      {jc.vehicle_rego && <span className="text-slate-500 ml-2">{jc.vehicle_rego}</span>}
                    </span>
                    <span className="text-slate-500 text-xs">{jc.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <p className="text-xs text-slate-400 italic">
          Leads become real customers automatically when the first invoice is created from their job card. Click "Promote to Customer" if you want to flag this caller as a customer before invoicing (rare).
        </p>
      </div>
    </Layout>
  );
}
